import React, { useCallback, useState } from "react";
import { ScanBarcode } from "lucide-react";
import { CircularProgress } from "@mui/material";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

// import { useTrackingNumberValidator } from "./useTrackingNumberValidator";
import { useWarehouseContext } from "./WarehouseProvider";
import {
  applyImageUpdates,
  populateWarehouseDataFromTakeback,
  useWarehouse,
} from "./useWarehouse";
import { getFreshAccessToken } from "api/use-user";

import { ReactComponent as ClearSvg } from "./svgs/close.svg";
import { StepHeader } from "./components/StepHeader";
import { clsx } from "clsx";
import { FullscreenModal } from "./components/FullscreenModal";
import { useConnectionStatus } from "hooks/useConnectionStatus";
import { TakebackQueueIcon } from "./components/TakebackQueueIcon";
import { useTakebackLookup } from "api/tes/useTakebackLookup";
import { useSteps } from "./useSteps";
import { stepsRequiredOnCheckIn, isStepValid } from "./useConfiguration";
import { BottomNav } from "./components/BottomNav";
import scanPackageIcon from "./images/scan-package-icon.png";
import { Summary } from "./Summary";
import { IncompatiblePackageModal } from "./components/IncompatiblePackageModal";
import { StorageRouting } from "./StorageRouting";
import { WarehouseButtonsFooter } from "./components/WarehouseButtonsFooter";
import { ControlWeightCapture } from "./ControlWeightCapture";

const ERROR_ID_NOT_FOUND = "Case cannot be identified, please contact support";

export const PackageLookup = ({
  onInventoryClick,
  onAlertsClick,
  initialTrackingId,
  successMessage,
}) => {
  const { checkInPackage, startProcessingPackage, setCurrentTakeback } =
    useWarehouseContext();
  const { setHasCheckedContents, setTakeback, takeback, cachedReturnMethod } =
    useWarehouse();
  const {
    checkInSteps,
    stepIndex,
    processCheckInSteps,
    jumpTo,
    dropStoreMode,
    resetToPackageLookup,
  } = useSteps();
  const [trackingId, setTrackingId] = useState(initialTrackingId || "");
  const [showIncompatibleModal, setShowIncompatibleModal] = useState(false);

  // Auto-trigger Start when initialTrackingId is provided
  React.useEffect(() => {
    if (initialTrackingId && initialTrackingId !== "") {
      // Small delay to ensure component is fully mounted
      setTimeout(() => {
        onStartButtonClick();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTrackingId]);

  // Add styles to contain scanner video
  React.useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      #reader video {
        object-fit: cover !important;
        width: 100% !important;
        height: 100% !important;
        max-height: 100vh !important;
      }
      #reader {
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        overflow: hidden !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [error, setError] = useState("");
  const [checkedInSuccess, setCheckedInSuccess] = useState(
    !!takeback || !!successMessage
  );
  const [alreadyCheckedIn, setAlreadyCheckedIn] = useState(false);
  const [checkingInPackage, setCheckingInPackage] = useState(false);
  const [processingPackage, setProcessingPackage] = useState(false);
  const [scanBarCode, setScanBarCode] = useState(false);

  // const trackingNumberValidator = useTrackingNumberValidator();

  const getTakeback = useTakebackLookup();

  const trackingInputCallback = useCallback(
    (inputElement) => {
      if (inputElement || trackingId === "") {
        inputElement?.focus();
      }
    },
    [trackingId]
  );

  const [html5QrCodeInstance, setHtml5QrCodeInstance] = useState(null);

  const onBarScan = () => {
    setError("");
    setScanBarCode(true);
    const html5QrCode = new Html5Qrcode("reader");
    setHtml5QrCodeInstance(html5QrCode);

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const minDimension = Math.min(viewportWidth, viewportHeight);

    const qrboxSize = Math.min(Math.floor(minDimension * 0.8), 600);

    html5QrCode.start(
      {},
      {
        fps: 10,
        qrbox: { width: qrboxSize, height: qrboxSize / 2 },
        formatsToSupport: [Html5QrcodeSupportedFormats.CODE_128],
        videoConstraints: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      },
      (decodedText) => {
        // if it starts with 1Z then it's a UPS tracking number
        if (decodedText.startsWith("1Z")) {
          setTrackingId(decodedText);
          setScanBarCode(false);
          html5QrCode.stop();
          setHtml5QrCodeInstance(null);
          // Auto-trigger the Start button functionality with the scanned ID
          setTimeout(() => {
            onStartButtonClick(decodedText);
          }, 100);
        }
      }
    );
  };

  const onStartButtonClick = async (scannedTrackingId = null) => {
    setError("");

    const trackingIdToUse = scannedTrackingId || trackingId;

    if (!Boolean(trackingIdToUse)) {
      setError("Please enter a Tracking number");
      return;
    }

    // Set loading state immediately
    setCheckingInPackage(true);

    try {
      // First, get the takeback to check its status
      const currentTakeback = await getTakeback(trackingIdToUse);

      if (!currentTakeback) {
        setError(ERROR_ID_NOT_FOUND);
        setCheckingInPackage(false);
        return;
      }

      // Check package compatibility with current mode
      const data =
        typeof currentTakeback?.data === "string"
          ? JSON.parse(currentTakeback?.data ?? "{}")
          : currentTakeback?.data;

      const returnMethod = data?.returnMethod;
      const isMailPackage = returnMethod === "mail";
      const isDropStorePackage =
        returnMethod !== "mail" && returnMethod !== undefined;

      // Check compatibility
      if (dropStoreMode && isMailPackage) {
        setShowIncompatibleModal(true);
        setCheckingInPackage(false);
        return;
      } else if (!dropStoreMode && isDropStorePackage) {
        setShowIncompatibleModal(true);
        setCheckingInPackage(false);
        return;
      }

      const isCheckedIn =
        currentTakeback.takeback_processing_history_list?.some(
          (history) => history.status === "checked-in"
        );

      const isVerified = currentTakeback.takeback_processing_history_list?.some(
        (history) =>
          history.status === "verified" ||
          history.status === "warehouse-verified-post-dropoff"
      );

      if (isVerified) {
        setError(
          `This package has already been processed. If this is a new package then set aside and contact Pentatonic with this information: ${currentTakeback.id} and the tracking number on the package`
        );
        setCheckingInPackage(false);
        return;
      }

      if (!isCheckedIn) {
        // onCheckInPackage will handle its own loading state
        setCheckingInPackage(false);
        await onCheckInPackage(currentTakeback);
      } else {
        // Clear checking in, set processing
        setCheckingInPackage(false);
        await onProcessPackage(currentTakeback);
      }
    } catch (err) {
      console.error("Error in onStartButtonClick:", err);
      setCheckingInPackage(false);

      // Handle different error types
      if (
        err.message?.includes("GraphQL request failed") ||
        err.message?.includes("GraphQL errors") ||
        err.message?.includes("Cannot read properties")
      ) {
        setError(ERROR_ID_NOT_FOUND);
      } else if (err.message?.includes("Failed to fetch")) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError(err.message || ERROR_ID_NOT_FOUND);
      }
    }
  };

  const onCheckInPackage = async (currentTakeback) => {
    resetToPackageLookup();
    setCheckingInPackage(true);
    setAlreadyCheckedIn(false);
    setError("");

    try {
      const lastTakeback = useWarehouse.getState().takeback;

      if (!currentTakeback) {
        setError(ERROR_ID_NOT_FOUND);
      } else {
        const isReceived =
          currentTakeback.takeback_processing_history_list?.some(
            (history) => history.status === "checked-in"
          );

        if (
          (lastTakeback?.id === trackingId ||
            trackingId === localStorage.getItem("lastCheckedInPackage")) &&
          !isReceived
        ) {
          setAlreadyCheckedIn(true);
          setCheckingInPackage(false);
          return;
        }

        if (isReceived) {
          setTrackingId("");
        } else {
          const requiredCheckInSteps = stepsRequiredOnCheckIn(
            currentTakeback
          ).filter((step) => !isStepValid(step));
          setTakeback(currentTakeback);

          if (requiredCheckInSteps.length) {
            // Add storage routing to the end of required steps
            const allSteps = [...requiredCheckInSteps, StorageRouting];
            processCheckInSteps(allSteps, () => {
              checkInPackage(currentTakeback);
              setTrackingId("");
              localStorage.setItem("lastCheckedInPackage", currentTakeback.id);
            });
            return;
          }

          // Show storage routing even if no other check-in steps required
          processCheckInSteps([StorageRouting], () => {
            checkInPackage(currentTakeback);
            setTrackingId("");
            localStorage.setItem("lastCheckedInPackage", currentTakeback.id);
          });
        }
      }
    } catch (err) {
      setError(err.message);
    }

    setCheckingInPackage(false);
  };

  const onProcessPackage = async (currentTakeback) => {
    setProcessingPackage(true);
    setCheckedInSuccess(false);

    try {
      if (!currentTakeback) {
        setError(ERROR_ID_NOT_FOUND);
        setProcessingPackage(false);
        return;
      }

      setHasCheckedContents(true);
      const isReceived = currentTakeback.takeback_processing_history_list?.some(
        (history) => history.status === "checked-in"
      );

      const isRejected =
        currentTakeback.takeback_processing_history_list?.at(-1)?.status ===
        "rejected";

      // allow processing of rejected packages
      if (!isReceived && !isRejected) {
        setError(`Please check-in parcel`);
        setProcessingPackage(false);
        return;
      }

      const isVerified = currentTakeback.takeback_processing_history_list?.some(
        (history) =>
          history.status === "verified" ||
          history.status === "warehouse-verified-post-dropoff"
      );

      if (isVerified) {
        setError(
          `This package has already been processed. If this is a new package then set aside and contact Pentatonic with this information: ${currentTakeback.id} and the tracking number on the package`
        );
      } else {
        // Populate warehouse data from existing takeback
        populateWarehouseDataFromTakeback(currentTakeback);
        await applyImageUpdates(getFreshAccessToken(), currentTakeback);
        setTakeback(currentTakeback);
        startProcessingPackage(currentTakeback);
        setCurrentTakeback(currentTakeback);

        const data =
          typeof currentTakeback?.data === "object"
            ? currentTakeback?.data
            : JSON.parse(currentTakeback?.data || "{}");
        const hasRejectCategory = data?.rejectCategory;

        if (hasRejectCategory && isReceived) {
          jumpTo(Summary); // Jump directly to Summary for checked-in rejected items
        } else {
          jumpTo(ControlWeightCapture);
        }
      }
    } catch (e) {
      setError(e.message);
    }

    setProcessingPackage(false);
  };

  const clearTrackingId = () => {
    setTrackingId("");
    setError("");
  };

  const isBusy = checkingInPackage || processingPackage;
  const isOnline = useConnectionStatus();

  const RequiredCheckInStep = checkInSteps?.[stepIndex] || null;

  return (
    <FullscreenModal>
      <div className="flex flex-col h-full">
        <StepHeader
          showModal={false}
          showLogIssueIcon={true}
          showUserIcon={!RequiredCheckInStep && !scanBarCode}
          noExit={!scanBarCode}
          rightSlot={!scanBarCode && <TakebackQueueIcon />}
          isLoading={isBusy}
          isCameraActive={scanBarCode}
          isScanning={scanBarCode}
          isPackageLookup={true}
          onExit={
            scanBarCode
              ? () => {
                  setScanBarCode(false);
                  if (html5QrCodeInstance) {
                    html5QrCodeInstance.stop();
                    setHtml5QrCodeInstance(null);
                  }
                }
              : undefined
          }
        />

        {RequiredCheckInStep && <RequiredCheckInStep />}

        <div
          id="reader"
          className="w-full h-full !absolute -z-10 overflow-hidden"
          style={{ maxHeight: "100vh" }}
        ></div>

        {!RequiredCheckInStep && (
          <>
            <div className="flex-1 flex flex-col items-center justify-center">
              {!scanBarCode && (
                <div className="flex flex-col items-center gap-6 max-w-[470px]">
                  <div className="flex flex-col items-center gap-4">
                    <img
                      src={scanPackageIcon}
                      alt="Scan Package"
                      className="w-[168px] h-[168px]"
                    />
                    <div className="flex flex-col items-center gap-[6px]">
                      <h1 className="text-[29px] font-bold text-white leading-[37px] tracking-[-0.01em]">
                        Scan Item
                      </h1>
                      <p className="text-base font-medium text-[#C6CCE8] leading-[22px] text-center">
                        Scan UPS barcode for check-in or processing. Applicable
                        for mail-in, full stores, and drop-off stores packages.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={`px-8 ${scanBarCode ? "pb-8" : "pb-[140px]"}`}>
              <div className="flex flex-col gap-8 w-full relative">
                <div className="border-l-4 border-l-[#00CFA8] bg-[#0B1C47] text-white p-4 rounded-lg empty:hidden absolute -translate-y-full w-full -top-4">
                  {isBusy
                    ? ""
                    : alreadyCheckedIn
                      ? "Already checked-in"
                      : !checkedInSuccess
                        ? ""
                        : successMessage
                          ? successMessage
                          : (() => {
                              // Try to get returnMethod from takeback data first, then fall back to cached
                              const data = takeback?.data
                                ? typeof takeback.data === "string"
                                  ? JSON.parse(takeback.data)
                                  : takeback.data
                                : null;
                              const returnMethod =
                                data?.returnMethod || cachedReturnMethod;

                              if (returnMethod === "mail") {
                                return "Thanks for checking in the parcel";
                              } else if (
                                returnMethod &&
                                returnMethod.includes("in_store")
                              ) {
                                return "Send to In-store drop-off pre-processing area";
                              } else {
                                return "Send to the In-store Full experience pre-processing area";
                              }
                            })()}
                </div>

                {!isOnline && (
                  <div className="bg-[#C12960] bg-opacity-20 flex rounded-lg w-full overflow-hidden">
                    <div className="h-full w-[5px] bg-[#C12960]"></div>
                    <div className="flex flex-col p-4">
                      <div className="text-[#DAE3F9] text-sm">
                        No internet connection. Restore the connection to finish
                        this take back.
                      </div>
                    </div>
                  </div>
                )}

                {!scanBarCode && (
                  <div className="flex gap-8 w-full">
                    <div className="flex-1 relative">
                      <input
                        ref={trackingInputCallback}
                        className={clsx(
                          "rounded-lg px-4 py-[26px] pr-24 border-2 focus:outline-none focus:ring-0 w-full bg-transparent text-white placeholder-[#8086A0] text-base h-[80px]",
                          {
                            "border-[#C12960]": error,
                            "border-[#282F44]": !error,
                          }
                        )}
                        type="text"
                        value={trackingId}
                        placeholder="Tracking number"
                        disabled={isBusy}
                        onChange={(e) => {
                          setError("");
                          setTrackingId(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !isBusy && isOnline) {
                            e.preventDefault();
                            onStartButtonClick();
                          }
                        }}
                      />
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                        {trackingId && (
                          <button
                            className="p-2"
                            onClick={() => clearTrackingId()}
                          >
                            <ClearSvg
                              style={{ width: "20px", height: "20px" }}
                            />
                          </button>
                        )}
                        <button className="p-2" onClick={onBarScan}>
                          <ScanBarcode className="w-7 h-7 text-[#A1A7C2]" />
                        </button>
                      </div>
                    </div>

                    <button
                      className="rounded-xl disabled:cursor-not-allowed disabled:bg-[#282F44] bg-[#173991] text-white hover:bg-[#2256DB] font-bold text-[21px] flex-1 h-[80px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.08),inset_0px_-1px_1px_0px_rgba(0,0,0,0.16)]"
                      disabled={isBusy || !isOnline}
                      onClick={() => {
                        onStartButtonClick();
                      }}
                    >
                      {isBusy ? <CircularProgress size={24} /> : "Start"}
                    </button>
                  </div>
                )}
                {error && (
                  <div className="text-base text-[#C12960] -mt-6">{error}</div>
                )}
              </div>
            </div>
          </>
        )}

        {!RequiredCheckInStep && !scanBarCode && (
          <BottomNav
            activeTab="processing"
            onInventoryClick={onInventoryClick}
            onAlertsClick={onAlertsClick}
          />
        )}

        {scanBarCode && (
          <WarehouseButtonsFooter isCameraActive>
            <button
              onClick={() => {
                setScanBarCode(false);
                if (html5QrCodeInstance) {
                  html5QrCodeInstance.stop();
                  setHtml5QrCodeInstance(null);
                }
              }}
              className="w-full rounded-lg font-normal py-3.5 m-0 border border-transparent bg-white text-black hover:bg-gray-200"
            >
              Cancel
            </button>
          </WarehouseButtonsFooter>
        )}
      </div>

      <IncompatiblePackageModal
        isOpen={showIncompatibleModal}
        onClose={() => {
          setShowIncompatibleModal(false);
          setTrackingId("");
        }}
      />
    </FullscreenModal>
  );
};
